import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShell } from './ShellContext';
import { leagueMeta } from './leagueUtils';
import { ChevronDownIcon, RailToggleIcon } from './icons';
import { SleeperAvatar } from './SleeperAvatar';

const TopBar = ({ title, actions, hasRail, onToggleRail, onToggleSheet, onOpenPalette, toast }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { leagues, contextLeague, canSwitchLeague, requestLeagueSwitch, sleeperUser } = useShell();
  const [menuOpen, setMenuOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(false);
  const chipRef = useRef(null);
  const accRef = useRef(null);

  useEffect(() => {
    if (!accOpen) return undefined;
    const onClick = (e) => {
      if (accRef.current && !accRef.current.contains(e.target)) setAccOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [accOpen]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (chipRef.current && !chipRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const pickLeague = (league) => {
    requestLeagueSwitch(league);
    setMenuOpen(false);
  };

  // The chip only exists while a league-scoped view is on screen (simulator,
  // canvas, listing). The dropdown only opens when that view registered a
  // switch handler — there is no global "active league" to set anymore.
  const canOpenMenu = canSwitchLeague && leagues.length > 0;

  return (
    <header className="ft-topbar">
      <div className="ft-viewtitle">{title}</div>
      {contextLeague && (
        <div ref={chipRef} className="ft-ctxwrap" style={{ position: 'relative' }}>
          <button
            className="ft-ctxchip"
            onClick={() => (canOpenMenu ? setMenuOpen((open) => !open) : null)}
            style={canOpenMenu ? undefined : { cursor: 'default' }}
          >
            <span className="ft-ctxdot" />
            <span className="ft-ctxname">{contextLeague.name}</span>
            {canOpenMenu ? <ChevronDownIcon /> : null}
          </button>
          {menuOpen && (
            <div className="ft-leaguemenu">
              {leagues.map((league) => (
                <button key={league.leagueId} onClick={() => pickLeague(league)}>
                  <span>
                    {league.name}
                    <span className="ft-lmeta" style={{ display: 'block' }}>
                      {leagueMeta(league)} · {league.season}
                    </span>
                  </span>
                  {contextLeague.leagueId === league.leagueId ? (
                    <span className="ft-lcheck">Viewing</span>
                  ) : null}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="ft-spacer" />
      {actions ? <div className="ft-tbactions">{actions}</div> : null}
      <button className="ft-kbtn" onClick={onOpenPalette}>
        Jump to anything <kbd>⌘K</kbd>
      </button>
      {!user ? (
        <>
          {pathname.startsWith('/market') && (
            <span className="ft-savecap">Your portfolio lives in this browser</span>
          )}
          <button className="ft-btn sm" onClick={() => navigate('/login')}>Log in</button>
        </>
      ) : null}
      <button className="ft-railtoggle" onClick={onToggleRail} title="Toggle context rail">
        <RailToggleIcon />
      </button>
      {hasRail ? (
        <button className="ft-sheettoggle" onClick={onToggleSheet} title="Context">
          <RailToggleIcon />
        </button>
      ) : null}
      <div ref={accRef} className="ft-mobileacct" style={{ position: 'relative', display: user ? undefined : 'none' }}>
        <button
          className="ft-mobileacct-chip"
          onClick={() => (user ? setAccOpen((open) => !open) : navigate('/login'))}
        >
          {user?.sleeperUsername ? (
            <SleeperAvatar id={sleeperUser?.avatar} name={user.sleeperUsername} size={34} />
          ) : (
            'FT'
          )}
        </button>
        {accOpen && user ? (
          <div className="ft-accmenu" style={{ position: 'absolute', top: 44, right: 0, left: 'auto', bottom: 'auto', width: 210 }}>
            <div className="ft-acchead">
              <b>{user.sleeperUsername || 'Account'}</b>
              {user.email || 'Signed in'}
            </div>
            <button
              className="ft-accitem"
              onClick={async () => {
                setAccOpen(false);
                await logout();
                navigate('/');
              }}
            >
              <span>Log out</span>
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default TopBar;
